/**
 * Token Utility Functions
 * Pure functions for token operations - no hooks
 */

import { AUTH_STORAGE_KEYS } from '../types'
import type { AuthUser } from '../types'

// ============================================================================
// Storage Mode
// ============================================================================

const STORAGE_MODE_KEY = 'spcc-storage-mode'

export function isPersistentSession(): boolean {
  if (typeof window === 'undefined') return false
  return localStorage.getItem(STORAGE_MODE_KEY) === 'local'
}

/**
 * Returns sessionStorage or localStorage based on the rememberMe flag stored during login.
 */
function getActiveStorage(): Storage {
  if (typeof window === 'undefined') return sessionStorage
  return isPersistentSession() ? localStorage : sessionStorage
}

// ============================================================================
// Session Management
// ============================================================================

/**
 * Store complete session (token + user).
 * rememberMe=true  → localStorage (persists across browser close)
 * rememberMe=false → sessionStorage (cleared when tab/browser closes)
 */
export function storeSession(token: string, user: AuthUser, rememberMe = false): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(STORAGE_MODE_KEY, rememberMe ? 'local' : 'session')
  const storage = getActiveStorage()
  storage.setItem(AUTH_STORAGE_KEYS.TOKEN, token)
  storage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(user))
}

/**
 * Get stored session — reads from the correct storage (session or local).
 */
export function getStoredSession(): { token: string; user: AuthUser } | null {
  if (typeof window === 'undefined') return null
  const storage = getActiveStorage()
  const token = storage.getItem(AUTH_STORAGE_KEYS.TOKEN)
  const userJson = storage.getItem(AUTH_STORAGE_KEYS.USER)
  if (!token || !userJson) return null
  try {
    return { token, user: JSON.parse(userJson) as AuthUser }
  } catch {
    return null
  }
}

/**
 * Clear all auth data from both storages and the mode flag.
 */
export function clearAuthStorage(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem(STORAGE_MODE_KEY)
  const keys = [AUTH_STORAGE_KEYS.TOKEN, AUTH_STORAGE_KEYS.USER]
  keys.forEach(key => {
    localStorage.removeItem(key)
    sessionStorage.removeItem(key)
  })
}

// ============================================================================
// Token Helpers (convenience wrappers kept for backward compatibility)
// ============================================================================

export function storeToken(token: string): void {
  if (typeof window !== 'undefined') {
    getActiveStorage().setItem(AUTH_STORAGE_KEYS.TOKEN, token)
  }
}

export function getStoredToken(): string | null {
  if (typeof window !== 'undefined') {
    return getActiveStorage().getItem(AUTH_STORAGE_KEYS.TOKEN)
  }
  return null
}

export function removeToken(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN)
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN)
  }
}

export function storeUser(user: AuthUser): void {
  if (typeof window !== 'undefined') {
    getActiveStorage().setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(user))
  }
}

export function getStoredUser(): AuthUser | null {
  if (typeof window !== 'undefined') {
    const userJson = getActiveStorage().getItem(AUTH_STORAGE_KEYS.USER)
    if (userJson) {
      try {
        return JSON.parse(userJson) as AuthUser
      } catch {
        return null
      }
    }
  }
  return null
}

export function removeUser(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(AUTH_STORAGE_KEYS.USER)
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.USER)
  }
}

// ============================================================================
// Token Validation (Basic)
// ============================================================================

/**
 * Check if token exists and is not empty
 */
export function hasValidToken(): boolean {
  const token = getStoredToken()
  return !!token && token.length > 0
}

/**
 * Decode JWT payload (without verification)
 * Note: This is for client-side use only, not for security validation
 */
export function decodeTokenPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null

    const payload = parts[1]
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decoded)
  } catch {
    return null
  }
}

/**
 * Check if token is expired based on exp claim
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeTokenPayload(token)
  if (!payload || typeof payload.exp !== 'number') return true

  // Add 10 second buffer
  const now = Math.floor(Date.now() / 1000)
  return payload.exp < now + 10
}
